import React, { useState, Fragment } from 'react';
import { DateRange } from '../../types';
import { Menu, Transition } from '@headlessui/react';
import { HiCalendar, HiChevronDown } from 'react-icons/hi';
import { format } from 'date-fns';

interface DateRangeSelectorProps {
  dateRange: DateRange;
  onChange: (dateRange: DateRange) => void;
}

const presets: { label: string; value: DateRange['preset'] }[] = [
  { label: 'Today', value: 'today' },
  { label: 'Yesterday', value: 'yesterday' },
  { label: 'Last 7 Days', value: 'last7days' },
  { label: 'Last 30 Days', value: 'last30days' },
  { label: 'This Month', value: 'thisMonth' },
  { label: 'Last Month', value: 'lastMonth' },
];

const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  dateRange,
  onChange,
}) => {
  const [isCustom, setIsCustom] = useState(dateRange.preset === 'custom');

  const getDateRangeForPreset = (preset: DateRange['preset']): DateRange => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    switch (preset) {
      case 'today':
        return {
          startDate: format(today, 'yyyy-MM-dd'),
          endDate: format(today, 'yyyy-MM-dd'),
          preset,
        };
      case 'yesterday':
        return {
          startDate: format(yesterday, 'yyyy-MM-dd'),
          endDate: format(yesterday, 'yyyy-MM-dd'),
          preset,
        };
      case 'last7days': {
        const start = new Date(today);
        start.setDate(start.getDate() - 7);
        return {
          startDate: format(start, 'yyyy-MM-dd'),
          endDate: format(today, 'yyyy-MM-dd'),
          preset,
        };
      }
      case 'last30days': {
        const start = new Date(today);
        start.setDate(start.getDate() - 30);
        return {
          startDate: format(start, 'yyyy-MM-dd'),
          endDate: format(today, 'yyyy-MM-dd'),
          preset,
        };
      }
      case 'thisMonth': {
        const start = new Date(today.getFullYear(), today.getMonth(), 1);
        return {
          startDate: format(start, 'yyyy-MM-dd'),
          endDate: format(today, 'yyyy-MM-dd'),
          preset,
        };
      }
      case 'lastMonth': {
        const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const end = new Date(today.getFullYear(), today.getMonth(), 0);
        return {
          startDate: format(start, 'yyyy-MM-dd'),
          endDate: format(end, 'yyyy-MM-dd'),
          preset,
        };
      }
      default:
        return dateRange;
    }
  };

  const handlePresetChange = (preset: DateRange['preset']) => {
    setIsCustom(false);
    const newRange = getDateRangeForPreset(preset);
    onChange(newRange);
  };

  const getCurrentLabel = () => {
    if (isCustom || dateRange.preset === 'custom') {
      return `${dateRange.startDate} - ${dateRange.endDate}`;
    }
    return presets.find((p) => p.value === dateRange.preset)?.label || 'Select Range';
  };

  return (
    <Menu as="div" className="relative inline-block text-left">
      <Menu.Button className="btn btn-outline gap-2">
        <HiCalendar className="w-5 h-5" />
        <span className="hidden sm:inline">{getCurrentLabel()}</span>
        <HiChevronDown className="w-4 h-4" />
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 mt-2 w-56 origin-top-right rounded-xl bg-white dark:bg-dark-800 shadow-hard border border-gray-200 dark:border-dark-700 focus:outline-none overflow-hidden z-50">
          <div className="py-1">
            {presets.map((preset) => (
              <Menu.Item key={preset.value}>
                {({ active }) => (
                  <button
                    onClick={() => handlePresetChange(preset.value)}
                    className={`${
                      active
                        ? 'bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white'
                        : 'text-gray-700 dark:text-gray-300'
                    } ${
                      dateRange.preset === preset.value
                        ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 font-semibold'
                        : ''
                    } group flex w-full items-center px-4 py-2.5 text-sm transition-colors`}
                  >
                    {preset.label}
                  </button>
                )}
              </Menu.Item>
            ))}
          </div>
          
          <div className="border-t border-gray-200 dark:border-dark-700 p-3 bg-gray-50 dark:bg-dark-900/50">
            <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Custom Range
            </p>
            <div className="space-y-2">
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => {
                  setIsCustom(true);
                  onChange({
                    ...dateRange,
                    startDate: e.target.value,
                    preset: 'custom',
                  });
                }}
                className="input text-xs w-full"
              />
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => {
                  setIsCustom(true);
                  onChange({
                    ...dateRange,
                    endDate: e.target.value,
                    preset: 'custom',
                  });
                }}
                className="input text-xs w-full"
              />
            </div>
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

export default DateRangeSelector;
